import { useState, useEffect } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useCampaign } from '../contexts/CampaignContext'
import { useModal } from '../contexts/ModalContext'
import MarkdownText from '../components/MarkdownText'
import './FeywildPage.css'

function FeywildPage() {
  const { user, authEpoch } = useAuth()
  const { campaign, canEdit, isModuleEnabled, loading: campaignLoading } = useCampaign()
  const navigate = useNavigate()
  const { alert: showAlert, confirm } = useModal()

  // Access state
  const [hasAccess, setHasAccess] = useState(false)
  const [accessLoading, setAccessLoading] = useState(true)
  const [character, setCharacter] = useState(null)

  // Powers state (admin)
  const [powers, setPowers] = useState([])
  const [loadingPowers, setLoadingPowers] = useState(false)
  const [editingPower, setEditingPower] = useState(null)
  const [newPower, setNewPower] = useState({ d100_value: '', effect: '' })

  // Roll log state (admin)
  const [rollLog, setRollLog] = useState([])
  const [loadingRollLog, setLoadingRollLog] = useState(false)

  // Roll state (player)
  const [isRolling, setIsRolling] = useState(false)
  const [lastRoll, setLastRoll] = useState(null)
  const [lastEffect, setLastEffect] = useState(null)
  const [rollAnimation, setRollAnimation] = useState(false)

  // Check access on mount
  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    checkAccess()
    // authEpoch: see the identical comment in components/tabs/ItemsTab.jsx.
  }, [user, canEdit, authEpoch])

  // Load admin data when access is granted
  useEffect(() => {
    if (hasAccess && canEdit) {
      fetchPowers()
      fetchRollLog()
    }
  }, [hasAccess, canEdit, authEpoch])

  const checkAccess = async () => {
    try {
      const response = await fetch('/api/feyblood/access')
      const data = await response.json()

      setHasAccess(data.hasAccess)
      if (data.character) {
        setCharacter(data.character)
      }
      setAccessLoading(false)
    } catch (err) {
      console.error('Access check failed:', err)
      setHasAccess(false)
      setAccessLoading(false)
    }
  }

  const fetchPowers = async () => {
    setLoadingPowers(true)
    try {
      const response = await fetch('/api/feyblood/powers')
      const data = await response.json()
      // A non-OK response (e.g. a 401) still has a JSON body (`{error:
      // "..."}`), not an array — handing that straight to setPowers() would
      // make powers.map() below throw during render, and with no
      // ErrorBoundary in this app that unmounts the entire tree, re-auth
      // modal included. See the identical guard in ItemsTab.jsx etc.
      setPowers(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Failed to fetch powers:', err)
    }
    setLoadingPowers(false)
  }

  const fetchRollLog = async () => {
    setLoadingRollLog(true)
    try {
      const response = await fetch('/api/feyblood/rolls')
      const data = await response.json()
      // Same non-array guard as fetchPowers above — rollLog.map() below
      // would otherwise throw on a non-OK response's JSON error body.
      setRollLog(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Failed to fetch roll log:', err)
    }
    setLoadingRollLog(false)
  }


  const handleAddPower = async () => {
    if (!newPower.d100_value.trim() || !newPower.effect.trim()) {
      showAlert({ title: 'Error', message: 'Both d100 and Effect are required', type: 'error' })
      return
    }

    try {
      const response = await fetch('/api/feyblood/powers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPower)
      })

      if (response.ok) {
        setNewPower({ d100_value: '', effect: '' })
        fetchPowers()
        showAlert({ title: 'Success', message: 'Power added successfully', type: 'success' })
      } else {
        const data = await response.json()
        showAlert({ title: 'Error', message: data.error || 'Failed to add power', type: 'error' })
      }
    } catch (err) {
      showAlert({ title: 'Error', message: 'Failed to add power', type: 'error' })
    }
  }

  const handleUpdatePower = async () => {
    if (!editingPower.d100_value.trim() || !editingPower.effect.trim()) {
      showAlert({ title: 'Error', message: 'Both d100 and Effect are required', type: 'error' })
      return
    }

    try {
      const response = await fetch('/api/feyblood/powers', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingPower)
      })

      if (response.ok) {
        setEditingPower(null)
        fetchPowers()
        showAlert({ title: 'Success', message: 'Power updated successfully', type: 'success' })
      } else {
        const data = await response.json()
        showAlert({ title: 'Error', message: data.error || 'Failed to update power', type: 'error' })
      }
    } catch (err) {
      showAlert({ title: 'Error', message: 'Failed to update power', type: 'error' })
    }
  }

  const handleDeletePower = async (power) => {
    await confirm({
      title: 'Delete Power',
      message: `Are you sure you want to delete the power for ${power.d100_value}?`,
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/feyblood/powers', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: power.id })
          })

          if (response.ok) {
            fetchPowers()
            showAlert({ title: 'Success', message: 'Power deleted', type: 'success' })
          } else {
            showAlert({ title: 'Error', message: 'Failed to delete power', type: 'error' })
          }
        } catch (err) {
          showAlert({ title: 'Error', message: 'Failed to delete power', type: 'error' })
        }
      }
    })
  }

  const handleClearRollLog = async () => {
    await confirm({
      title: 'Clear Roll Log',
      message: 'Are you sure you want to clear the entire roll log? This cannot be undone.',
      confirmText: 'Clear All',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/feyblood/rolls', { method: 'DELETE' })
          if (response.ok) {
            setRollLog([])
            showAlert({ title: 'Success', message: 'Roll log cleared', type: 'success' })
          }
        } catch (err) {
          showAlert({ title: 'Error', message: 'Failed to clear roll log', type: 'error' })
        }
      }
    })
  }

  const handleRoll = async () => {
    if (isRolling) return

    setIsRolling(true)
    setRollAnimation(true)

    try {
      const response = await fetch('/api/feyblood/roll', { method: 'POST' })

      const data = await response.json()

      // Delay to show animation
      setTimeout(() => {
        setLastRoll(data.roll)
        setLastEffect(data.matched ? data.effect : null)
        setRollAnimation(false)
        setIsRolling(false)

        // Refresh roll log if admin
        if (canEdit) {
          fetchRollLog()
        }
      }, 1000)
    } catch (err) {
      setRollAnimation(false)
      setIsRolling(false)
      showAlert({ title: 'Error', message: 'Failed to roll', type: 'error' })
    }
  }

  // `campaignLoading` gates the module check: CampaignProvider fetches
  // /api/campaigns asynchronously, so `campaign` (and therefore
  // isModuleEnabled) is briefly null/false on every fresh load. Redirecting
  // on that transient state would bounce a legitimate feyblood page straight
  // back to the campaign page before the real data ever arrives.
  if (campaignLoading) {
    return null
  }

  if (!isModuleEnabled('feyblood')) {
    return <Navigate to={campaign ? `/c/${campaign.slug}/campaign` : '/campaigns'} replace />
  }

  // Loading state
  if (accessLoading) {
    return (
      <div className="feyblood-page">
        <div className="feyblood-container">
          <div className="feyblood-loading">
            <div className="loading-spinner"></div>
            <p>Checking access...</p>
          </div>
        </div>
      </div>
    )
  }

  // Access denied
  if (!hasAccess) {
    return (
      <div className="feyblood-page">
        <div className="feyblood-container">
          <div className="feyblood-denied">
            <div className="denied-icon">🚫</div>
            <h1>Access Denied</h1>
            <p>Your DM has not granted you access to the Feyblood system.</p>
            <button
              className="back-button"
              onClick={() => navigate(`/c/${campaign.slug}/campaign`)}
            >
              ← Back to Campaign
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="feyblood-page">
      <div className="feyblood-container">
        {/* Header */}
        <div className="feyblood-header">
          <button
            className="back-button"
            onClick={() => navigate(`/c/${campaign.slug}/campaign`)}
          >
            ← Back to Campaign
          </button>
          <div className="header-content">
            <h1>
              <span className="fey-icon">✨</span>
              Feyblood Powers
            </h1>
            <p className="subtitle">
              {canEdit ? 'Manage Feyblood effects and view roll history' : 'Roll to discover your Feyblood effect'}
            </p>
          </div>
        </div>

        {/* Admin View */}
        {canEdit ? (
          <div className="admin-view">
            {/* Access is granted per user in Campaign Settings → Feyblood. It
                used to be derived from a character holding a configurable
                secret, edited from a Settings panel that stood here; that gate
                is retired (see migration 0025) and the panel with it. */}
            <div className="feyblood-section">
              <div className="section-header">
                <h2>Access</h2>
              </div>
              <div className="settings-content">
                <span className="setting-value">
                  Grant players access in Campaign Settings → 🍄 Feyblood.
                </span>
                <button
                  className="edit-settings-btn"
                  onClick={() => navigate(`/c/${campaign.slug}/settings`)}
                >
                  Open Campaign Settings
                </button>
              </div>
            </div>

            {/* Powers Table */}
            <div className="feyblood-section">
              <div className="section-header">
                <h2>Feyblood Powers Table</h2>
              </div>

              {loadingPowers ? (
                <p className="loading-text">Loading powers...</p>
              ) : (
                <>
                  <table className="powers-table">
                    <thead>
                      <tr>
                        <th style={{ width: '120px' }}>d100</th>
                        <th>Effect</th>
                        <th style={{ width: '120px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {powers.map(power => (
                        <tr key={power.id}>
                          <td>
                            {editingPower?.id === power.id ? (
                              <input
                                type="text"
                                value={editingPower.d100_value}
                                onChange={(e) => setEditingPower({ ...editingPower, d100_value: e.target.value })}
                                placeholder="e.g., 1-5 or 50"
                              />
                            ) : (
                              power.d100_value
                            )}
                          </td>
                          <td>
                            {editingPower?.id === power.id ? (
                              <textarea
                                value={editingPower.effect}
                                onChange={(e) => setEditingPower({ ...editingPower, effect: e.target.value })}
                                placeholder="Effect description..."
                              />
                            ) : (
                              <MarkdownText>{power.effect}</MarkdownText>
                            )}
                          </td>
                          <td>
                            {editingPower?.id === power.id ? (
                              <div className="row-actions">
                                <button className="save-btn" onClick={handleUpdatePower}>Save</button>
                                <button className="cancel-btn" onClick={() => setEditingPower(null)}>Cancel</button>
                              </div>
                            ) : (
                              <div className="row-actions">
                                <button className="edit-btn" onClick={() => setEditingPower({ ...power })}>Edit</button>
                                <button className="delete-btn" onClick={() => handleDeletePower(power)}>Delete</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Add New Power */}
                  <div className="add-power-form">
                    <h3>Add New Power</h3>
                    <div className="add-power-inputs">
                      <input
                        type="text"
                        placeholder="d100 (e.g., 1-5 or 50)"
                        value={newPower.d100_value}
                        onChange={(e) => setNewPower({ ...newPower, d100_value: e.target.value })}
                      />
                      <textarea
                        placeholder="Effect description..."
                        value={newPower.effect}
                        onChange={(e) => setNewPower({ ...newPower, effect: e.target.value })}
                      />
                      <button className="add-btn" onClick={handleAddPower}>
                        + Add Power
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Roll Log */}
            <div className="feyblood-section">
              <div className="section-header">
                <h2>Roll Log</h2>
                {rollLog.length > 0 && (
                  <button className="clear-log-btn" onClick={handleClearRollLog}>
                    Clear Log
                  </button>
                )}
              </div>

              {loadingRollLog ? (
                <p className="loading-text">Loading roll log...</p>
              ) : rollLog.length === 0 ? (
                <p className="empty-log">No rolls recorded yet.</p>
              ) : (
                <table className="roll-log-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Character</th>
                      <th>Roll</th>
                      <th>Effect</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rollLog.map(roll => (
                      <tr key={roll.id}>
                        <td>{new Date(roll.created_at).toLocaleString()}</td>
                        <td>{roll.character_name}</td>
                        <td className="roll-value">{roll.roll_value}</td>
                        <td>{roll.effect || <em>No effect found</em>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
          /* Player View */
          <div className="player-view">
            <div className="roll-section">
              {/* Effect Display Area */}
              <div className="effect-display">
                {lastEffect ? (
                  <div className="effect-content">
                    <h3>Your Feyblood Effect</h3>
                    <div className="effect-text">
                      <MarkdownText>{lastEffect}</MarkdownText>
                    </div>
                  </div>
                ) : lastRoll && !lastEffect ? (
                  <div className="effect-content no-effect">
                    <h3>No Effect Found</h3>
                    <p>Roll again to try for a different result.</p>
                  </div>
                ) : (
                  <div className="effect-placeholder">
                    <div className="placeholder-icon">🌿</div>
                    <p>Roll the d100 to discover your Feyblood effect...</p>
                  </div>
                )}
              </div>

              {/* Roll Button Area */}
              <div className="roll-area">
                <button
                  className={`roll-button ${isRolling ? 'rolling' : ''}`}
                  onClick={handleRoll}
                  disabled={isRolling}
                >
                  {isRolling ? (
                    <span className="rolling-text">Rolling...</span>
                  ) : (
                    <>
                      <span className="dice-icon">🎲</span>
                      <span>Roll for Feyblood</span>
                    </>
                  )}
                </button>

                {/* Roll Result */}
                {lastRoll !== null && (
                  <div className={`roll-result ${rollAnimation ? 'animating' : ''}`}>
                    <span className="roll-label">You rolled:</span>
                    <span className="roll-number">{lastRoll}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FeywildPage
